// ─── A flight recorder for "it froze" ────────────────────────────────────────
//
// Drea's phone froze twice after the camera; closing the browser app fixed it, a
// refresh did not. The server log cannot see inside the page, so this does: a short
// trail of what the page was doing (taps on the capture tiles, the camera handing a
// file back, the page being hidden / frozen / restored, errors, losing signal, and a
// heartbeat that shows whether the page's own thread had stopped). The trail is kept
// on the phone as it goes, and sent to the server at the NEXT page load, so a page
// that froze solid still tells us what happened — the page itself cannot.
//
// No photo, text, file name or address is ever recorded: only event names, sizes and
// timings. It is read with:  journalctl -u nulifepa | grep '\[diag\]'

const KEY = "nlpa.diag";
const PENDING = "nlpa.diag.pending";
const MAX_CRUMBS = 80;

type Crumb = { t: number; e: string; d?: string };
type Trail = { sid: string; start: number; lastBeat: number; path: string; crumbs: Crumb[] };

const read = <T,>(key: string): T | null => { try { const v = localStorage.getItem(key); return v ? JSON.parse(v) as T : null; } catch { return null; } };
const write = (key: string, v: unknown) => { try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* no storage: no trail */ } };

let trail: Trail | null = null;
const crumb = (e: string, d?: string) => {
  if (!trail) return;
  trail.crumbs.push({ t: Date.now() - trail.start, e, d: d?.slice(0, 80) });
  if (trail.crumbs.length > MAX_CRUMBS) trail.crumbs.splice(1, trail.crumbs.length - MAX_CRUMBS);
  write(KEY, trail);
};

/** Worth reporting if the page was doing something we are trying to understand, or
 *  if it did not end the way a page normally does. */
function worthSending(t: Trail): boolean {
  const names = t.crumbs.map(c => c.e);
  return names.some(n => /^(tile|keep|file|error|reject|freeze|resume|discarded)/.test(n)) || names[names.length - 1] !== "pagehide";
}

async function send(payload: unknown): Promise<boolean> {
  try {
    const res = await fetch("/api/diag", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload), keepalive: true });
    return res.ok;
  } catch { return false; }
}

export function startDiagnostics(): void {
  if (typeof window === "undefined" || trail) return;
  const previous = read<Trail>(KEY);
  const pending = read<unknown[]>(PENDING) ?? [];
  const nav = (performance.getEntriesByType?.("navigation")?.[0] as PerformanceNavigationTiming | undefined)?.type ?? "?";
  const now = {
    nav, wasDiscarded: !!(document as unknown as { wasDiscarded?: boolean }).wasDiscarded, visible: document.visibilityState,
    standalone: !!(navigator as unknown as { standalone?: boolean }).standalone, ua: navigator.userAgent.slice(0, 160),
    screen: `${screen.width}x${screen.height}@${window.devicePixelRatio}`, online: navigator.onLine,
  };

  trail = { sid: Math.random().toString(36).slice(2, 8), start: Date.now(), lastBeat: Date.now(), path: location.pathname, crumbs: [] };
  crumb("load", `${nav}${now.wasDiscarded ? " DISCARDED" : ""}`);

  // Report what the PREVIOUS page did, now that there is a page able to say so.
  const outgoing = [...pending];
  if (previous && worthSending(previous)) outgoing.push({ prev: previous, silentForMs: Date.now() - previous.lastBeat });
  if (outgoing.length) {
    void (async () => {
      const failed: unknown[] = [];
      for (const p of outgoing.slice(-3)) if (!(await send({ ...(p as object), now }))) failed.push(p);
      write(PENDING, failed.slice(-3));
    })();
  }

  const on = (target: EventTarget, name: string, label = name, detail?: (ev: Event) => string | undefined, opts?: AddEventListenerOptions) =>
    target.addEventListener(name, ev => crumb(label, detail?.(ev)), opts);

  on(document, "visibilitychange", "visibility", () => document.visibilityState);
  on(window, "pagehide", "pagehide", ev => ((ev as PageTransitionEvent).persisted ? "persisted" : "gone"));
  on(window, "pageshow", "pageshow", ev => ((ev as PageTransitionEvent).persisted ? "from-bfcache" : "fresh"));
  on(window, "blur", "blur");
  on(window, "focus", "focus");
  on(document, "freeze", "freeze");
  on(document, "resume", "resume");
  on(window, "online", "online");
  on(window, "offline", "offline");
  window.addEventListener("error", ev => crumb("error", `${ev.message} @${(ev.filename ?? "").split("/").pop()}:${ev.lineno}`));
  window.addEventListener("unhandledrejection", ev => crumb("reject", String((ev.reason as Error)?.message ?? ev.reason)));
  // The capture controls: taps, and the camera handing a file back (sizes only).
  document.addEventListener("click", ev => {
    const id = (ev.target as HTMLElement | null)?.closest?.("[data-testid]")?.getAttribute("data-testid");
    if (id && /^(tile|keep|capture|outbox|pick)/.test(id)) crumb(`tile:${id}`);
  }, true);
  document.addEventListener("change", ev => {
    const el = ev.target as HTMLInputElement | null;
    if (el?.type === "file") crumb("file", `${el.files?.length ?? 0} file(s), ${Math.round((el.files?.[0]?.size ?? 0) / 1024)} KB, ${el.files?.[0]?.type ?? "?"}`);
  }, true);

  // A heartbeat: if the page's own thread stops, the beats stop, and the next
  // report says for how long.
  setInterval(() => { if (trail) { trail.lastBeat = Date.now(); write(KEY, trail); } }, 2000);
}
